import { useEffect, useState } from "react";

export interface DataStoreApi<GetData, SetData = GetData> {
  getData: () => GetData;
  setData: (data: SetData) => void;
  updateData: (fn: (current: GetData) => SetData) => void;
  useData: () => GetData;
  waitUntilDefined: () => Promise<void>;
  useDataSuspense: () => Exclude<GetData, undefined>;
}

export function createDataStore<T>(
  initialValue?: undefined
): DataStoreApi<T | undefined, T>;
export function createDataStore<T>(initialValue: T): DataStoreApi<T, T>;
export function createDataStore<T>(
  initialValue?: T
): DataStoreApi<T | undefined, T> {
  let currentData = initialValue;
  const storeListeners: ((dataStore: T | undefined) => void)[] = [];
  let resolveReady: (() => void) | undefined;
  let readyPromise = new Promise<void>((resolve) => {
    resolveReady = resolve;
  });
  if (currentData !== undefined) {
    resolveReady?.();
  }

  function getData(): T | undefined {
    return currentData;
  }

  function setData(data: T | undefined): void {
    currentData = data;
    if (currentData !== undefined) {
      resolveReady?.();
    } else {
      readyPromise = new Promise<void>((resolve) => {
        resolveReady = resolve;
      });
    }
    for (const listener of storeListeners) {
      listener(currentData);
    }
  }

  function updateData(fn: (current: T | undefined) => T): void {
    setData(fn(currentData));
  }

  function useData(): T | undefined {
    const [internalData, setInternalData] = useState(currentData);
    // biome-ignore lint/correctness/useExhaustiveDependencies: <no-explanation>
    useEffect(() => {
      // In case the rev of the data store changed between the time we did the `useState`
      // and the time of the `useEffect` we need to refresh manually the state.
      if (internalData !== currentData) {
        setInternalData(currentData);
      }
      // Register the state setter to be called for any subsequent data store change
      storeListeners.push(setInternalData);
      return () => {
        storeListeners.splice(storeListeners.indexOf(setInternalData), 1);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return internalData;
  }

  function waitUntilDefined(): Promise<void> {
    return currentData === undefined ? readyPromise : Promise.resolve();
  }

  function useDataSuspense(): Exclude<T, undefined> {
    const data = useData();
    if (data === undefined) {
      throw waitUntilDefined();
    }
    return data as Exclude<T, undefined>;
  }

  return {
    getData,
    setData,
    updateData,
    useData,
    waitUntilDefined,
    useDataSuspense,
  };
}
